/* eslint-disable no-shadow */
import React, {
  useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Loader from '../../components/Loader';
import EventDetailsPage from '../../components/EventDetailsPage';
import { getEventDetails } from './EventDetailsAPI';
import eventTypes from '../../constants/eventTypes';
import { transformEventPackages } from '../EventBookingContainer/EventBookingAPI';
import appContext from '../../constants/appContext';
import containerQueryProps from '../../props/containerQueryProps';
import { getTranslation } from '../../providers/TranslationProvider';
import { setMetadataPageTitle } from '../../reducers/resyRouterSlice';
import Logger from '../../utils/logger';

const { WIDGET, CONSUMER } = appContext;
const { SEATED } = eventTypes;

const eventDetailsContainerPropTypes = {
  $rootScope: PropTypes.shape({
    matchMedia: PropTypes.shape({
      sm: PropTypes.bool,
    }),
    metadata: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image_twitter: PropTypes.string,
      image_facebook: PropTypes.string,
    }),
    $broadcast: PropTypes.func,
    $digest: PropTypes.func,
  }).isRequired,
  $routeParams: PropTypes.shape({
    location: PropTypes.string.isRequired,
    eventSlug: PropTypes.string.isRequired,
    previewToken: PropTypes.string,
    venueSlug: PropTypes.string.isRequired,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  containerQuery: containerQueryProps,
  context: PropTypes.oneOf([WIDGET, CONSUMER]),
  day: PropTypes.string,
  login: PropTypes.func,
  notifyService: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  previewMode: PropTypes.bool,
  resyToken: PropTypes.string,
  sameDay: PropTypes.bool,
  selectResy: PropTypes.func.isRequired,
  setMetadataPageTitle: PropTypes.func.isRequired,
  showBookNowBtn: PropTypes.bool,
  tableConfigId: PropTypes.string,
  userIsGda: PropTypes.bool,
  venueId: PropTypes.number,
};

const getEventDetailsPayload = (
  eventSlug,
  location,
  seats,
  resyToken,
  timezone,
  token,
  venueSlug,
  venueMaxMinSizeDetails
) => ({
  event_url_slug: eventSlug,
  location,
  // eslint-disable-next-line camelcase
  num_seats: seats,
  resyToken,
  timezone,
  ...token,
  venue_url_slug: venueSlug,
  venueMaxMinSizeDetails,
});

const getMostAvailable = (ticketTypes, event) => {
  const mostAvailable = ticketTypes.reduce((acc, curr) => (curr.availableQuantity > acc
    ? curr.availableQuantity
    : acc), 0);

  if (mostAvailable > event.maxPartySize) return event.maxPartySize;

  return mostAvailable;
};

const getVenueFromEvent = (event) => event?.venueData || {};

const EventDetailsContainer = (props) => {
  const {
    $rootScope,
    $routeParams,
    Analytics,
    containerQuery,
    context,
    day,
    login,
    notifyService,
    previewMode,
    resyToken,
    sameDay,
    selectResy,
    showBookNowBtn,
    tableConfigId,
    userIsGda,
    setMetadataPageTitle,
  } = props;

  const {
    location, venueSlug, eventSlug, previewToken,
  } = $routeParams;

  const [loaded, setLoaded] = useState(false);
  const [event, setEvent] = useState({});
  const [safetyMeasures, setSafetyMeasures] = useState([]);
  const [safetyMeasureUrl, setSafetyMeasureUrl] = useState(undefined);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [showNotify, setShowNotify] = useState(false);
  const [enableBookNow, setEnableBookNow] = useState(true);
  const [venue, setVenue] = useState({});
  const [selectedSeats, setSelectedSeats] = useState(undefined);

  const handleError = useCallback((err) => {
    Logger.error(err);

    if (err?.status >= 400) {
      window.location.pathname = '/';
    }
  }, []);

  const setMetadata = useCallback((venueData, eventData) => {
    const venueName = venueData.name;
    const eventName = eventData.title;
    const title = getTranslation({
      translationKey: 'titles.event_details',
      translationValues: { eventName, venueName },
      fallback: `Book Your ${eventName} Event With ${venueName} on Resy`,
    });
    $rootScope.metadata.title = title;
    setMetadataPageTitle(title);

    $rootScope.metadata.description = eventData.description || '';

    if (Array.isArray(eventData.images) && eventData.images.length > 0) {
      const [url] = eventData.images;
      $rootScope.metadata.image_twitter = url;
      $rootScope.metadata.image_facebook = url;
    }
    $rootScope.$digest();
  }, [$rootScope, setMetadataPageTitle]);

  const handleSuccess = useCallback((eventData, requestedSeats) => {
    const eventMinSeats = Number.parseInt(eventData?.minPartySize, 10) || 2;
    const seatsForSelection = Number.parseInt(requestedSeats, 10) || eventMinSeats;
    const venueData = getVenueFromEvent(eventData);

    setSelectedSeats((currentSelectedSeats) => {
      const parsedCurrentSeats = Number.parseInt(currentSelectedSeats, 10);
      return Number.isInteger(parsedCurrentSeats) ? parsedCurrentSeats : eventMinSeats;
    });
    setMetadata(venueData, eventData);

    const transformedTicketTypes = transformEventPackages(eventData.packages);
    const mostAvailable = getMostAvailable(transformedTicketTypes, eventData);
    const shouldShowNotify = eventData.type === SEATED
      ? !mostAvailable
      : seatsForSelection > mostAvailable;

    setLoaded(true);
    setEvent(eventData);
    setSafetyMeasures(venueData.safety_categories);
    setSafetyMeasureUrl(venueData.safety_info_url);
    setShowNotify(shouldShowNotify);
    setTicketTypes(transformedTicketTypes);
    setVenue({
      ...venueData,
      urlSlug: venueData.url_slug,
    });
  }, [
    setMetadata,
  ]);

  const fetchEventDetails = useCallback(async (requestedSeats) => {
    const parsedRequestedSeats = Number.parseInt(requestedSeats, 10);
    const hasExplicitRequestedSeats = Number.isInteger(parsedRequestedSeats);
    const seats = hasExplicitRequestedSeats ? parsedRequestedSeats : 1;

    if (previewMode && !previewToken) {
      window.location.pathname = '/';
      return;
    }

    const token = previewMode && previewToken ? { previewToken } : {};

    try {
      if (hasExplicitRequestedSeats) {
        const eventWithSelectedPackages = await getEventDetails(
          getEventDetailsPayload(
            eventSlug,
            location,
            seats,
            resyToken,
            undefined,
            token,
            venueSlug
          )
        );
        handleSuccess(eventWithSelectedPackages, seats);
        setEnableBookNow(true);
        return;
      }

      const initialEvent = await getEventDetails(
        getEventDetailsPayload(
          eventSlug,
          location,
          seats,
          resyToken,
          undefined,
          token,
          venueSlug
        )
      );
      const eventMinSeats = Number.parseInt(initialEvent?.minPartySize, 10) || seats;
      const eventWithMinPackages = await getEventDetails(
        getEventDetailsPayload(
          eventSlug,
          location,
          eventMinSeats,
          resyToken,
          initialEvent?.timezone,
          token,
          venueSlug
        )
      );
      handleSuccess(eventWithMinPackages, seats);
      setEnableBookNow(true);
    } catch (err) {
      handleError(err);
    }
  }, [
    eventSlug,
    handleError,
    handleSuccess,
    location,
    previewMode,
    previewToken,
    resyToken,
    venueSlug,
  ]);

  useEffect(() => {
    fetchEventDetails();
  }, [
    fetchEventDetails,
  ]);

  const onTicketQuantitySelected = useCallback((e) => {
    const nextSeats = Number.parseInt(e?.currentTarget?.value, 10);
    if (Number.isInteger(nextSeats)) {
      setSelectedSeats(nextSeats);
      fetchEventDetails(nextSeats);
    }
    setEnableBookNow(false);
    setTicketTypes([]);
  }, [fetchEventDetails]);

  const {
    date,
    isOnline,
    needToKnowCopy,
    type,
  } = event;
  const seats = selectedSeats || Number.parseInt(event?.minPartySize, 10) || 2;

  if (!loaded) return <Loader containerHeight="500px" />;

  let additionalProps = {};
  if (context === WIDGET) {
    additionalProps = {
      copyPosition: 'bottom',
      showBottomSection: false,
      showFooter: true,
    };
  }

  // `date` is the current event's own date for details/booking content.
  // `day` is an optional filter used by same-day upcoming events queries.
  return (
    <EventDetailsPage
      $rootScope={$rootScope}
      Analytics={Analytics}
      city={location}
      containerQuery={containerQuery}
      context={context}
      date={date}
      day={day}
      eventSlug={eventSlug}
      event={event}
      fetchEventDetails={fetchEventDetails}
      isOnline={isOnline}
      isMobile={!$rootScope.matchMedia.sm}
      login={login}
      needToKnowCopy={needToKnowCopy}
      notifyService={notifyService}
      noIndex={event.noIndex}
      onTicketQuantitySelect={onTicketQuantitySelected}
      enableBookNow={enableBookNow}
      previewMode={previewMode}
      safetyMeasures={event.isOnline ? undefined : safetyMeasures}
      safetyMeasureUrl={event.isOnline ? undefined : safetyMeasureUrl}
      sameDay={sameDay}
      seats={+seats || 2}
      selectResy={selectResy}
      showAbout={context !== WIDGET}
      showBookNowBtn={showBookNowBtn}
      showNotify={showNotify}
      tableConfigId={tableConfigId}
      ticketTypes={ticketTypes}
      type={type}
      userIsGda={userIsGda}
      venue={venue}
      venueSlug={venueSlug}
      {...additionalProps}
    />
  );
};

EventDetailsContainer.propTypes = eventDetailsContainerPropTypes;
EventDetailsContainer.defaultProps = {
  containerQuery: {},
  context: CONSUMER,
  day: undefined,
  login: null,
  previewMode: false,
  notifyService: null,
  sameDay: undefined,
  tableConfigId: null,
  resyToken: null,
  showBookNowBtn: true,
  userIsGda: false,
  venueId: undefined,
};

EventDetailsContainer.displayName = 'EventDetailsContainer';

const mapDispatchToProps = {
  setMetadataPageTitle,
};

export { eventDetailsContainerPropTypes };
export default connect(null, mapDispatchToProps)(EventDetailsContainer);
export { EventDetailsContainer as ComponentUnderTest };
